import Component from '../abstracts/Component';
import {isMobile, MobileBreakpoint} from '../utils/MobileUtils';
import TrackingComponent from '../components/TrackingComponent';
import { addPreloadParam } from '../utils/SearchUrlUtil';

export default class FiltersComponent extends Component {
  constructor(htmlEl) {
    super(htmlEl);
    // initializing elements
    this.filterTrigger = this.$component.querySelector(this.SELECTORS.filterTrigger);
    this.filterLayer = this.$component.querySelector(this.SELECTORS.filterLayer);
    this.filterClose = this.$component.querySelector(this.SELECTORS.filterClose);
    this.filterWrapper = this.$component.querySelector(this.SELECTORS.filterWrapper);
    this.filterItems = this.$component.querySelectorAll(this.SELECTORS.filterItem);
    this.filterItemCheckbox = this.$component.querySelectorAll(this.SELECTORS.filterItemCheckbox);
    this.filterReset = this.$component.querySelector(this.SELECTORS.filterReset);
    this.filterResetMobile = this.$component.querySelector(this.SELECTORS.filterResetMobile);
    this.filterApply = this.$component.querySelector(this.SELECTORS.filterApply);
    this.filterCounterBadge = this.$component.querySelector(this.SELECTORS.filterCounterBadge);
    this.filterItemCounterBadge = this.$component.querySelectorAll(this.SELECTORS.filterItemCounterBadge);
    this.header = document.querySelector(this.SELECTORS.header);
    this.pageSize = parseInt(document.querySelector(this.SELECTORS.pageSize)?.dataset?.pageSize);
    this.searchText = document.querySelector(this.SELECTORS.searchText)?.dataset?.searchText;
    this.pagination = document.querySelector(this.SELECTORS.pagination);
    this.trackingComponent = new TrackingComponent();

    // initializing variables
    this.sortBy = '';
    this.url = '';
    this.listingLayout = (document.querySelector(this.SELECTORS.focusGrid)?.dataset.gridFocus == 'true');
  }

  get SELECTORS() {
    return {
      filterTrigger: '[data-trigger-filters]',
      filterLayer: '[data-filter-layer]',
      filterClose: '[data-filter-close]',
      filterWrapper: '[data-filter-wrapper]',
      filterItem: '[data-filter-item]',
      filterItemLayer: '[data-filter-item-layer]',
      filterItemCheckbox: '[data-filter-checkbox]',
      filterReset: '[data-filter-reset]',
      filterResetMobile: '[data-filter-reset-mobile]',
      filterApply: '[data-filter-apply]',
      filterCounterBadge: '[data-filters-counter-badge]',
      filterItemCounterBadge: '[data-filter-item-count]',
      header: '[data-js-component="HeaderComponent"]',
      checkbox: 'input[type="checkbox"]',
      listingProducts: '[data-filters-result]',
      pageSize: '[data-page-size]',
      productCard: '[data-product-card]',
      searchText: '[data-search-text]',
      pagination: '[data-pagination]',
      focusGrid: '[data-grid-focus]',
      wishBtn: '[data-wishlist-btn]',
      liveChatFloatingBtn: '#embeddedMessagingConversationButton',
      personalAssistantBtn: '.bs-ew-popup-container',
      paginationElement: '[data-pagination]',
      paginationLink: '[data-page-link]',
      sortBy: '[data-sort]',
      sortByLayer: '[data-sort-layer-desktop]',
      infiniteScroll: '[data-infinite-scroll]'
    }
  }

  get CLASSES() {
    return {
      disabled: 'disabled',
      hidden: '--hidden',
      notAllowed: '--not-allowed',
      open: 'open',
      zIndex2: '--z-2',
      focus: 'focus',
      focus4: 'focus-4',
      focus9: 'focus-9',
      focus22: 'focus-22',
      focus27: 'focus-27',
      searchPage: 't-search'
    }
  }

  /**
   * Appends new products to the product listing container.
   * Parses the provided HTML response to extract product elements,
   * clears the current product listing, and appends the new products.
   * Updates the browser history with a new URL.
   *
   * @param {String} res - The HTML response containing updated product information.
   */
  appendProducts(res) {
    // parse response into HTML
    const response = new DOMParser().parseFromString(res, 'text/html');
    // replace history with new URL
    this.replacePageInHistory(this.url, {}, '', true);

    // get products container and remove old children
    const container = document.querySelector(this.SELECTORS.listingProducts);

    // get new products from result
    const responseResult = response.querySelector(this.SELECTORS.listingProducts);
    const products = responseResult.children;
    this.trackingComponent.actions.loadMore(products);
    // if we are in search page
    if (document.body.classList.contains(this.CLASSES.searchPage)) {
      this.trackingComponent.actions.loadMoreSearchPage(products);
    }

    // append new products to container
    Array.from(products).forEach(product => container.append(product));

    // re-initialize product components for new products
    // This is necessary to ensure that any dynamic components (like WishlistComponent) are initialized
    this.initializeProductComponents(container);
  }

  /**
   * Returns a query string for the current state of the filters component.
   * This query string is in the format of key1:value1:key2:value2.
   * The values of the query string are derived from the state of the
   * checkboxes in the filter component.
   *
   * @return {String} A query string for the current state of the filters component.
   */
  buildQuery() {
    if (this.filterWrapper) {
      let replaceString = new RegExp('[=\&]', 'g');
      return $(this.filterWrapper).serialize().replace(replaceString, ':');
    } else {
      return;
    }
  }

  /**
   * Returns a full query string for the current state of the filters component.
   * This query string is in the format of key1:value1:key2:value2.
   * The values of the query string are derived from the state of the
   * checkboxes in the filter component.
   *
   * @param {String} textSearch - The text to search
   * @param {Number} size - The number of items to show per page
   * @param {String} query - The current query string for the filters
   * @param {Number} page - The current page
   * @param {String} sort - The method to sort by
   * @param {String} listingLayout - The current listing layout
   * @return {String} A query string for the current state of the filters component.
   */
  buildFullQuery({textSearch = '', size = 36, query = '', page, sort = 'topRated', listingLayout = 'false', preload = false}) {
    let composedQuery = (query && query !== '') ? `:${sort}:${query}` : `:${sort}`;
    if (textSearch && textSearch !== '') {
      composedQuery = `${textSearch}${composedQuery}`;
    }
    const queryString = [
      textSearch && `text=${textSearch}`,
      `sort=${sort}`,
      `size=${size}`,
      `focus=${listingLayout}`,
      page && `page=${page}`,
      'isRefineSearch=true',
      `q=${composedQuery}&resetQuery=true`,
      preload && `preload=true`,
    ].filter(Boolean).join('&');

    return `?${queryString}`;
  }

  /**
   * Close the mobile layer of the filters component
   */
  closeMobileLayer() {
    this.filterLayer.classList.remove(this.CLASSES.open);
    this.header.classList.remove(this.CLASSES.zIndex2);
    // show login popup floating button
    this.$emit(this.$customEvents.LOGIN_POPUP_EVENTS.showFloatingIcon);
    // show live chat floating button
    // execute here for now cause I don't know where should be
    const liveChatFloatingBtn = document.querySelector(this.SELECTORS.liveChatFloatingBtn);
    if (liveChatFloatingBtn) liveChatFloatingBtn.style.display = 'block';


    const personalAssistantBtn = document.querySelector(this.SELECTORS.personalAssistantBtn);
    personalAssistantBtn?.classList.remove(this.CLASSES.hidden);
  }

  /**
   * Given a query string and a page number, returns an AJAX promise which
   * resolves with the content of the page.
   *
   * @param {String} query - The query string
   * @param {Number} pagequery - The page number
   * @param {String} searchtext - The text to search
   * @param {Boolean} navigate - Whether to navigate to the page or just return the promise
   * @return {Promise} A promise that resolves with the content of the page
   */
  getPage(query, pagequery, searchtext, navigate = false, preload = false) {
    this.sortBy = this.sortByParams();
    let fullQuery = '';
    fullQuery = this.buildFullQuery({
      textSearch: searchtext || this.searchText || null,
      size: this.pageSize || 36,
      query,
      page: pagequery,
      sort: this.sortBy,
      listingLayout: false,
      preload
    });
    this.url = window.location.origin + window.location.pathname + fullQuery;

    if (navigate) {
      window.location.href = this.url;
      return;
    }

    const ajaxConf = {
      type: 'GET',
      url: this.url
    }

    return $.ajax(ajaxConf);
  }

  /**
   * Manages the focus state of product cards in the listing.
   *
   * If the focus parameter is true, it adds specific focus-related classes
   * to product cards at certain indices to highlight them. Otherwise, it
   * removes all focus-related classes from all product cards.
   *
   * @param {Boolean} focus - A boolean indicating whether to apply or remove
   *                          focus styles to specific product cards.
   */
  manageFocus(focus) {
    const productCards = document.querySelectorAll(this.SELECTORS.productCard);
    if (focus) {
      productCards.forEach((card, index) => {
        switch (index) {
          case 4:
            card.classList.add(this.CLASSES.focus);
            card.classList.add(this.CLASSES.focus4);
            break;
          case 9:
            card.classList.add(this.CLASSES.focus);
            card.classList.add(this.CLASSES.focus9);
            break;
          case 22:
            card.classList.add(this.CLASSES.focus);
            card.classList.add(this.CLASSES.focus22);
            break;
          case 27:
            card.classList.add(this.CLASSES.focus);
            card.classList.add(this.CLASSES.focus27);
            break;
          default:
            break;
        }
      });
    } else {
      productCards.forEach(card => card.classList.remove(this.CLASSES.focus));
      productCards.forEach(card => card.classList.remove(this.CLASSES.focus4));
      productCards.forEach(card => card.classList.remove(this.CLASSES.focus9));
      productCards.forEach(card => card.classList.remove(this.CLASSES.focus22));
      productCards.forEach(card => card.classList.remove(this.CLASSES.focus27));
    }
  }

  /**
   * Open the mobile layer of the filters component
   * and adjust the header's z-index for proper display.
   */
  openMobileLayer() {
    // close sort by layer
    this.$emit(this.$customEvents.SORT_BY_EVENTS.close);
    this.filterLayer.classList.add(this.CLASSES.open);
    this.header.classList.add(this.CLASSES.zIndex2);
    // hide login popup floating button
    this.$emit(this.$customEvents.LOGIN_POPUP_EVENTS.hideFloatingIcon);
    // hide live chat floating button
    // execute here for now cause I don't know where should be
    const liveChatFloatingBtn = document.querySelector(this.SELECTORS.liveChatFloatingBtn);
    if (liveChatFloatingBtn) {
      liveChatFloatingBtn.style.display = 'none';
    }

    const personalAssistantBtn = document.querySelector(this.SELECTORS.personalAssistantBtn);
    personalAssistantBtn?.classList.add(this.CLASSES.hidden);

  }

  /**
   * Replace the current page in the browser's history with the given URL.
   * If history.scrollRestoration is set to "manual", it will be reset to "auto"
   * after calling this function.
   *
   * @param {String} url - The URL to replace the current page in the browser's history with.
   * @param {Object} additionalStateData - The data to be passed to the new page in the browser's history.
   * @param {String} title - The title of the new page.
   * @param {Boolean} needPreload - Whether to set history.scrollRestoration to "manual".
   */
  replacePageInHistory(url, additionalStateData, title, needPreload) {
    const history = window.history;
    if (history && history.replaceState && url && url.trim().length > 0) {
      if (needPreload) {
        url = addPreloadParam(url);
      }
      // update focus param
      if (url.includes('focus')) {
        const index = url.indexOf('focus');
        const endIndex = url.indexOf('&', index);
        const oldParam = url.substring(index, endIndex);
        const replacement = `focus=${this.listingLayout}`;
        url = url.substring(0, index) + replacement + url.substring(index + oldParam.length);
      }
      history.replaceState(additionalStateData, title || window.document.title, url);
      if (additionalStateData && additionalStateData.focusElement) {
        history.scrollRestoration = 'manual';
      }
    }
  }

  /**
   * Resets the current filters and fetches the updated product list.
   * This function triggers an AJAX request to get the page data,
   * and then updates the products and filters on the page based on the response.
   * All existing filters will be cleared and the product list will be refreshed.
   */
  resetFilters() {
    this.getPage(null, null, this.searchText).then(res => {
      this.updateProductsAndFilters(res, true);
    })
  }

  /**
   * Gets the current sort value from the page.
   * @return {String} The current sort value.
   */
  sortByParams() {
    return document.querySelector('[data-sort]')?.dataset?.sort;
  }

  /**
   * Update the filters component with the given response.
   * If the 'reset' parameter is set to true, all filters will be deselected.
   * Otherwise, the method will check if the existing filters are present in the response
   * and update the component accordingly.
   *
   * @param {Object} response - The response to update the component with.
   * @param {Boolean} reset - Whether to reset all filters.
   */
  updateFilters(response, reset = false) {
    // get existing filters
    const originalCheckboxes = [];
    this.filterItems.forEach(item => {
      const itemLayer = item.querySelector(this.SELECTORS.filterItemLayer);
      const checkboxesArray = Array.from(itemLayer.querySelectorAll(this.SELECTORS.checkbox));
      originalCheckboxes.push(...checkboxesArray);
    });
    const selectedCheckboxesCounter = {};

    // if reset, simply deselect all checkboxes
    if (reset) {
      originalCheckboxes.forEach(checkbox => checkbox.checked = false);
    }
    // check presence of existing filter inside response
    originalCheckboxes.forEach(checkbox => {
      if (!checkbox.id.includes('subcategories')) {
        const isFilterActive = response.querySelector(`#${checkbox.id}`);
        if (isFilterActive) {
          // able filter
          checkbox.disabled = false;
          checkbox.parentElement.classList.remove(this.CLASSES.disabled);
          checkbox.parentElement.parentElement.classList.remove(this.CLASSES.notAllowed);
        } else {
          // disable filter
          checkbox.checked = false;
          checkbox.disabled = true;
          checkbox.parentElement.classList.add(this.CLASSES.disabled);
          checkbox.parentElement.parentElement.classList.add(this.CLASSES.notAllowed);
        }
      }

      if (checkbox.checked) {
        selectedCheckboxesCounter[checkbox.name] = selectedCheckboxesCounter[checkbox.name] || [];
        selectedCheckboxesCounter[checkbox.name].push(checkbox);
      }
      ;
    });

    let totCheckboxesCounter = 0;
    Object.keys(selectedCheckboxesCounter).forEach(key => {
      totCheckboxesCounter += selectedCheckboxesCounter[key].length;
    });

    if ((totCheckboxesCounter > 0) && !reset) {
      // manage reset button
      this.filterReset.classList.remove(this.CLASSES.hidden);

      // update filters counter badges
      this.filterCounterBadge.classList.remove(this.CLASSES.hidden);
      this.filterCounterBadge.querySelector('span').innerText = totCheckboxesCounter;
      this.filterItemCounterBadge.forEach(item => {
        const code = item.dataset.filterCode;
        if (selectedCheckboxesCounter[code]) {
          item.querySelector('span').innerText = selectedCheckboxesCounter[code].length;
          item.classList.remove(this.CLASSES.hidden);
          if (!isMobile(MobileBreakpoint.Max767)) {
            item.parentElement.querySelector('.--mobile-hidden').classList.add(this.CLASSES.hidden);
          }
        } else {
          item.classList.add(this.CLASSES.hidden);
          item.parentElement.querySelector('.--mobile-hidden').classList.remove(this.CLASSES.hidden);
        }
      });
    } else {
      // manage reset button
      this.filterReset?.classList.add(this.CLASSES.hidden);

      // manage filters counter badges
      this.filterCounterBadge?.classList.add(this.CLASSES.hidden);
      this.filterItemCounterBadge?.forEach(item => {
        item.classList.add(this.CLASSES.hidden);
        item.parentElement.querySelector('.--mobile-hidden').classList.remove(this.CLASSES.hidden);
      });
    }
  }

  /**
   * Updates the products and filters on the page based on the given response.
   * This function clears the current product listing, appends new products,
   * updates the browser history, updates results count, updates pagination
   * and refreshes the filters.
   * If the 'reset' parameter is true, all filters will be reset.
   * For mobile devices, it closes the filter layer after updating.
   *
   * @param {String} res - The HTML response containing updated product and filter information.
   * @param {Boolean} reset - Whether to reset all filters. Defaults to false.
   * @returns {Promise} A promise that resolves once the updates are complete.
   */
  updateProductsAndFilters(res, reset = false, needPreload = false) {
    return new Promise((resolve) => {
      // parse response into HTML
      const response = new DOMParser().parseFromString(res, 'text/html');
      // replace history with new URL
      this.replacePageInHistory(this.url, {}, '', needPreload);

      // get products container and remove old children
      const container = document.querySelector(this.SELECTORS.listingProducts);
      // clear existing products
      container.innerHTML = '';

      // get new products from result
      const responseResult = response.querySelector(this.SELECTORS.listingProducts);
      const products = responseResult.children;

      // append new products to container
      Array.from(products).forEach(product => container.append(product));

      // re-initialize product components for new products
      // This is necessary to ensure that any dynamic components (like WishlistComponent) are initialized
      this.initializeProductComponents(container);

      // if we are in mobile, close filter layer
      if (isMobile(MobileBreakpoint.Max767)) this.closeMobileLayer();

      // update filters available
      if (response.querySelector(this.SELECTORS.filterItemLayer)) {
        this.updateFilters(response, reset);
      }

      // update results number, if available
      const newSearchText = response.querySelector(this.SELECTORS.searchText);
      if (newSearchText) document.querySelector(this.SELECTORS.searchText).innerHTML = newSearchText.innerHTML;

      // hide infinite scroll, if necessary
      // else, reset it
      const infiniteScroll = response.querySelector(this.SELECTORS.infiniteScroll);
      if (!infiniteScroll) this.$emit(this.$customEvents.INFINITE_SCROLL_EVENTS.hide, parseInt(this.pageNumber) + 1);
      else this.$emit(this.$customEvents.INFINITE_SCROLL_EVENTS.reset, {updatedElement: infiniteScroll});

      // update pagination
      const newPagination = response.querySelector(this.SELECTORS.pagination);
      if (newPagination) {
        if (this.pagination) {
          this.pagination.classList.remove(this.CLASSES.hidden);
          this.pagination.innerHTML = newPagination.innerHTML;
        }
      } else {
        this.pagination && this.pagination.classList.add(this.CLASSES.hidden);
      }

      // resolve promise
      resolve();
    })
  }

  /**
   * Initialize product components after DOM update
   * @param {HTMLElement} container - The container with new product elements
   */
  async initializeProductComponents(container) {
    // Pagination
    const paginationElement = document.querySelector(this.SELECTORS.paginationElement);
    if (paginationElement) {
      const {default: PaginationComponent} = await import('./PaginationComponent');

      let paginationInstance = window.paginationComponentInstance;

      if (!paginationInstance) {
        paginationInstance = new PaginationComponent(paginationElement);
      }

      let paginationLinks = paginationElement.querySelectorAll(this.SELECTORS.paginationLink);
      if (paginationLinks && paginationLinks.length) {
        paginationLinks.forEach(link => {
          console.log({link})
          link.addEventListener('click', () => paginationInstance.goToPageFn(link));
        });
      }

      if (!window.paginationComponentInstance) {
        window.paginationComponentInstance = paginationInstance;
      }
    }

    try {
      // Initialize WishlistComponent for new wishlist buttons
      const wishlistButtons = container.querySelectorAll(this.SELECTORS.wishBtn);
      // If there are wishlist buttons, import the WishlistComponent dynamically
      // This allows for lazy loading of the component only when needed
      if (wishlistButtons.length > 0) {
        const { default: WishlistComponent } = await import('./WishlistComponent');
        // Try to find existing wishlist component instance. If a global instance exists, use it
        let wishlistInstance = window.wishlistComponentInstance;
        // If no global instance exists, create a temporary one
        if (!wishlistInstance) {
          wishlistInstance = new WishlistComponent();
        }
        // Add event listeners to new buttons
        wishlistButtons.forEach(button => {
          if (!button.dataset.initialized) {
            button.addEventListener('click', (event) => {
              wishlistInstance.handleSingleWishlistClick(event, button);
            });
            // Mark the button as initialized to avoid duplicate event listeners
            button.dataset.initialized = 'true';
          }
        });
        // Store global instance for future use.
        // This allows the WishlistComponent to be reused without re-initializing
        // If a global instance already exists, it will not be overwritten
        if (!window.wishlistComponentInstance) {
          window.wishlistComponentInstance = wishlistInstance;
        }
      }
    } catch (error) {
      console.error('Error initializing product components:', error);
    }
  }

  /**
   * Bind events to the FiltersComponent.
   * For mobile devices, it binds listeners to open/close filter layer,
   * close other filters, apply filter, and reset filters.
   * For desktop devices, it binds listeners to open/close filter item layer,
   * apply/cancel filter, and reset filters.
   * It also binds listener to update filters when the sort by value changes,
   * and when the listing layout changes.
   */
  bindEvents() {
    if (isMobile(MobileBreakpoint.Max767)) {
      // listeners to open/close filter layer
      this.filterTrigger && this.filterTrigger.addEventListener('click', () => {
        this.openMobileLayer();
      });

      this.filterClose && this.filterClose.addEventListener('click', () => {
        this.closeMobileLayer();
      });

      // listener to close other filters
      this.filterItems.length && this.filterItems.forEach(item => {
        const checkbox = item.querySelector(this.SELECTORS.checkbox);
        let otherFilters = Array.from(this.filterItems).filter(i => !(i.dataset.filterItem == item.dataset.filterItem));
        checkbox.addEventListener('click', () => {
          otherFilters.forEach(filter => {
            filter.querySelector(this.SELECTORS.checkbox).checked = false;
          });
        });
      });

      // listener to apply filter
      this.filterApply?.addEventListener('click', () => {
        const query = this.buildQuery();
        this.getPage(query, null, this.searchText).then(res => {
          this.updateProductsAndFilters(res, false);
        });
      });

      // listener to apply filter on checkbox click for tracking
      this.filterItemCheckbox.length && this.filterItemCheckbox.forEach(checkbox => {
        checkbox.addEventListener('click', () => {
          this.trackingComponent.actions.filterTracking(checkbox.name, checkbox.value);
        });
      });

    } else {
      // listeners to open/close filter item layer
      this.filterItems.length && this.filterItems.forEach(item => {
        const checkbox = item.querySelector(this.SELECTORS.checkbox);
        checkbox.addEventListener('click', () => {
          if (checkbox.checked) {
            // open filter item layer
            item.classList.add(this.CLASSES.open);
            // close others
            Array.from(this.filterItems).filter(i => i !== item).forEach(filterItem => {
              filterItem.classList.remove(this.CLASSES.open);
              filterItem.querySelector(this.SELECTORS.checkbox).checked = false;
            });
            // close sort by layer
            this.$emit(this.$customEvents.SORT_BY_EVENTS.close);
          } else {
            // close filter item layer
            item.classList.remove(this.CLASSES.open);
          }
        })
      })

      //listeners to apply/cancel filter
      this.filterItemCheckbox.length && this.filterItemCheckbox.forEach(checkbox => {
        checkbox.addEventListener('click', () => {
          this.trackingComponent.actions.filterTracking(checkbox.name, checkbox.value);
          const query = this.buildQuery();
          this.getPage(query, null, this.searchText).then(res => {
            this.updateProductsAndFilters(res, false).then(() => {
              window.scrollTo({
                top: 0,
                behavior: 'smooth'
              });
            });
          });
        });
      });
      // bind click outside filter to close filters
      document.addEventListener("click", (event) => {
        // check if i click inside the filter or sort
        if ( event.target.closest(this.SELECTORS.filterItem)
          || event.target.closest(this.SELECTORS.sortBy)
          || event.target.closest(this.SELECTORS.sortByLayer)
        ) {
          // prevent closing filter on modal's clicks
          return false; // Prevent to close on filter click
        }
        // if there is a open filter
        let openedFilter = document.querySelector('.w-filter-item.open');
        let openedSort = document.querySelector('.w-sort-by.open');
        // if open, close
        if (openedFilter || openedSort) {
          // close all filters
          Array.from(this.filterItems).forEach(filterItem => {
            filterItem.classList.remove(this.CLASSES.open);
            filterItem.querySelector(this.SELECTORS.checkbox).checked = false;
          });
          // close sortby layer
          this.$emit(this.$customEvents.SORT_BY_EVENTS.close);
        }
      });
    }

    this.$on(this.$customEvents.SORT_BY_EVENTS.changed, () => {
      this.trackingComponent.actions.filterTracking(this.sortByParams(), true);
      const query = this.buildQuery();
      this.getPage(query, null, this.searchText).then(res => {
        this.updateProductsAndFilters(res, false).then(() => {
          window.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
        });
      });
    });

    this.$on(this.$customEvents.FILTERS_EVENTS.closeItems, () => {
      this.filterItems.length && this.filterItems.forEach(item => {
        item.classList.remove(this.CLASSES.open);
        const checkbox = item.querySelector(this.SELECTORS.checkbox);
        checkbox.checked = false;
      });
    });

    this.$on(this.$customEvents.INFINITE_SCROLL_EVENTS.next, ({payload}) => {
      this.pageNumber = payload.page;
      const query = this.buildQuery();
      this.getPage(query, this.pageNumber).then(res => {
        this.appendProducts(res);
        this.$emit(this.$customEvents.INFINITE_SCROLL_EVENTS.loaded, {
          response: new DOMParser().parseFromString(res, 'text/html')
        });
      });
    });

    this.$on(this.$customEvents.LISTING_LAYOUT_EVENTS.changed, ({payload}) => {
      const query = this.buildQuery();
      if (payload.get('focus') == 'false') {
        this.listingLayout = 'false';
        this.getPage(query, this.pageNumber, null, false, true).then(res => {
          this.updateProductsAndFilters(res, false, true).then(() => this.manageFocus(false));
        });
      } else if (payload.get('focus') == 'true') {
        this.listingLayout = 'true';
        this.getPage(query, this.pageNumber, null, false, true).then(res => {
          this.updateProductsAndFilters(res, false, true).then(() => this.manageFocus(true));
        });
      }
    });

    this.$on(this.$customEvents.PAGINATION_EVENTS.goTo, (pageNum) => {
      this.pageNumber = pageNum.payload;
      const query = this.buildQuery();
      this.getPage(query, this.pageNumber, this.searchText, true);
    });

    // listeners to reset filters
    this.filterReset && this.filterReset.addEventListener('click', () => {
      this.resetFilters();
    });
    this.filterResetMobile && this.filterResetMobile.addEventListener('click', () => {
      this.resetFilters();
    });
  }

  render() {
    super.render();

    const searchQuery = new URLSearchParams(window.location.search);
    if (searchQuery.has('page')) {
      this.pageNumber = searchQuery.get('page');
    }

    this.bindEvents();
  }
}
